import { useState } from "react";
import { createPortal } from "react-dom";
import type { BoxAnswer, BoxConfirmationRequiredBody } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";

type PromptBox = BoxConfirmationRequiredBody["boxes"][number];

/**
 * G-84 (OD-47) — "Is this from box X?", one box at a time, Yes / No.
 *
 * The question fires when stock says the thing being sold, built or thrown
 * away sits in a box. Yes lets the sale take it from the box and records who
 * said so; No leaves the box alone and takes shelf stock instead — and if the
 * shelf has none the server refuses, naming the box. Both answers are logged.
 *
 * The floor's "card question" shape (`.shop-floor-scan-card`): one question,
 * big targets, portalled to the body because the panel behind it carries a
 * `backdrop-filter` (G-73).
 */
export function BoxPickPrompt({
  boxes,
  onAnswer,
  onCancel,
}: {
  boxes: PromptBox[];
  onAnswer(answers: BoxAnswer[]): void;
  onCancel(): void;
}) {
  const { t } = useT();
  const [index, setIndex] = useState(0);
  const [given, setGiven] = useState<BoxAnswer[]>([]);
  const box = boxes[index];
  if (!box) return null;

  const answer = (takenFromBox: boolean) => {
    const next = [...given, { boxId: box.boxId, takenFromBox }];
    if (index + 1 < boxes.length) {
      setGiven(next);
      setIndex(index + 1);
      return;
    }
    onAnswer(next);
  };

  return createPortal(
    <div className="shop-floor-scan-flow sf-scan-overlay" role="dialog" aria-modal="true" aria-label={t("boxPick.ariaLabel")}>
      <div className="shop-floor-scan-card">
        <p className="shop-floor-scan-title">
          {t("boxPick.question", {
            box: box.name ? `${box.code} (${box.name})` : box.code,
          })}
        </p>
        <ul className="sf-box-pick-items">
          {box.items.map((it) => (
            <li key={it.itemId}>
              <strong>{it.qty}</strong> × {it.itemName || t("boxPick.thisItem")}
            </li>
          ))}
        </ul>
        <p className="muted">{t("boxPick.explain")}</p>
        {boxes.length > 1 ? (
          <p className="muted">
            {t("boxPick.counter", { index: index + 1, total: boxes.length })}
          </p>
        ) : null}
        <div className="shop-floor-action-stack">
          <button type="button" className="button shop-floor-action-btn" onClick={() => answer(true)}>
            {t("boxPick.yes")}
          </button>
          <button type="button" className="button tinted shop-floor-action-btn" onClick={() => answer(false)}>
            {t("boxPick.no")}
          </button>
          <button type="button" className="button plain" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
